// list of fields that are present in all lookup-table entities
import { ApiFilter } from "@result/rui/utils/api";

import GenericPokkService from "#/data-layer/GenericPokkService";
import idb from "#/data-layer/idb";
import * as DLD from "#/def/data-layer";
import def from "#/def/lookup-tables.schema.gen";
import * as species from "#/utils/SpeciesScope";

// These flags help us filter lookup-table items by applicability
const $flags = ["goats", "sheeps", "horses", "cattle", "pigs"];
const envFlagsTrue = $flags.reduce((obj, k) => ({ ...obj, [k]: true }), {});
const envFlagsFalse = $flags.reduce((obj, k) => ({ ...obj, [k]: false }), {});

/**
 * By default, all applicability flags are true
 */
const envFlags = { ...envFlagsTrue };

export const apiFilterSpecies = (filterFunction, speciesKey = "species") => {
  if (!filterFunction) {
    return;
  }
  if (typeof filterFunction === "function") {
    const filter = filterFunction();
    if (filter instanceof ApiFilter) {
      const species = [
        envFlags.goats ? "KOZA" : undefined,
        envFlags.sheeps ? "OVCA" : undefined,
        envFlags.horses ? "KONJ" : undefined,
        envFlags.cattle ? "GOVEDO" : undefined,
        envFlags.pigs ? "PRASIC" : undefined,
      ].filter(Boolean);
      return filter.in(
        speciesKey,
        species.map((specie) => {
          switch (specie) {
            case "goats":
              return "KOZA";
            case "horses":
              return "KONJ";
            case "sheeps":
              return "KOZA";
            case "pigs":
              return "PRASIC";
            case "cattle":
              return "GOVEDO";
            default:
              return specie;
          }
        })
      );
    }
  }
};

/*
 *
 * @param {object} flags
 */
export function setLookupTableEnvFlags({ ...flags }) {
  Object.assign(envFlags, { ...envFlagsFalse, ...flags });
}

export function getLookupTableEnvFlags() {
  return { ...envFlags };
}

/**
 * Utility to validate lookup-table id.
 *
 * - This key is always of type string
 * - DB type: `varchar(30)`
 * - Value can not be empty or null.
 *
 * @param {mixed} id
 */
export function validateId(id) {
  let message = null;

  if (typeof id !== "string" || id.trim().length === 0) {
    message = "lookup-table id must be a non-empty string";
  } else if (id.length > 30) {
    message = "lookup-table id must not exceed 30 characters";
  } else if (id !== id.toUpperCase()) {
    message = "lookup-table id must be uppercase";
  }

  if (message) {
    /**
     * Working around error suppression in thunk actions
     */
    console.error(message);

    throw new Error(message);
  }
}

/**
 * Utility to validate lookup-table ident.
 *
 * @param {string} ident
 * @param {object} def
 */
export function validateIdent(ident, def) {
  let message = null;
  if (typeof ident !== "string") {
    message = `expecting string for lookup-table definition identifer, got  '${typeof ident}'`;
  } else if (!(ident in def)) {
    /**
     * Maybe later we will support other kinds too
     */
    message = `unexisting lookup-table ident, got '${ident}'`;
  }

  if (message) {
    /**
     * Working around error suppression in thunk actions
     */

    throw new Error(message);
  }
}

export function filtertByEnvironment(item) {
  return (
    (species.has(species.GOATS) && item.veljaZaKoze) ||
    (species.has(species.SHEEPS) && item.veljaZaOvce) ||
    (species.has(species.HORSES) && item.veljaZaKonje) ||
    (species.has(species.CATTLE) && item.veljaZaGovedo) ||
    (species.has(species.PIGS) && item.veljaZaPrasice)
  );
}

/**
 * Preloads ALL lookup tables
 *
 * We'll be utilizing /api/sif/all endpoint, which returns all lookup tables in a single request.
 * This is more efficient than preloading each table individually,
 * as it reduces the number of network requests and allows us to cache all tables at once.
 *
 * To achieve this, we will bypass DataLayer hooks
 * and use GenericPokkService & IndexedDB directly.
 *
 * This hook is intended to be used at the root of the app, to ensure that lookup tables are preloaded as early as possible,
 * and that the preloaded data is available in IndexedDB for the rest of the app to use.
 *
 * It will check if there are already items in the lookup tables store, and if so, it will skip preloading to avoid unnecessary network calls.
 *
 * @returns void
 */
export async function preload() {
  let count = await idb.count(DLD.LOOKUP_TABLES);

  if (count > 0) {
    // eslint-disable-next-line no-console
    console.debug("Lookup tables already preloaded, skipping.", {
      count,
    });
    return;
  }

  // initialize GenericPokkService at the root of the lookup-tables endpoints
  const svc = new GenericPokkService({ baseUrl: "/api/sif" });
  const rawItems = await svc.read("all");

  // remove duplicates
  //
  // at the moment, we have duplicates in the /api/sif/all endpoint response
  // bug reported: https://result-doo.atlassian.net/browse/POKK-643
  const items = [];
  const seen = new Set();
  for (const item of rawItems) {
    const key = `${item.ident} - ${item.sifra}`;
    if (!seen.has(key)) {
      seen.add(key);
      items.push(item);
    }
  }

  const $timestamp = new Date().toISOString();

  // this is intentianly copied from the idb's insert()
  // function to avoid pushing large arrays of items
  // and spreading it into func call
  await idb.txReadwrite(DLD.LOOKUP_TABLES, ({ store, resolve }) => {
    for (const { ident, ...item } of items) {
      try {
        store.put({ ...item, $ident: def[ident].path, $timestamp });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Error storing lookup table item", {
          item,
          error,
        });
      }
    }
    resolve();
  });

  count = await idb.count(DLD.LOOKUP_TABLES);
}
